"use client";

/* eslint-disable react-hooks/exhaustive-deps */
import { Definitions } from "@asosteam/asos-web-datalayer";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { getCookie } from "@repo/client-cookies";
import { useAuth } from "@repo/ui/auth";
import { useStore } from "@repo/ui/store";
import { useAnalytics } from "@/components/analytics/initialise-analytics";
import {
  AFFILIATE_ID_KEYS,
  APP_ENVIRONMENT,
  BUILD_NUMBER,
  CTA_REF_KEY,
} from "@/constants";
import { getUrlParameter } from "@/utils/get-url-parameter";
import { resolvePageIdentity } from "./resolve-page-identity";
import { useWebVitalsAnalytics } from "./use-web-vital-analytics";

const viewportDimensions = () => {
  const height = window.innerHeight || "",
    width = window.innerWidth || "";
  return typeof width === "number" && typeof height === "number"
    ? width + ":" + height
    : null;
};

export type PageLoadAnalyticsProps = {
  browseCurrency: NonNullable<Definitions["pageData"]>["browseCurrency"];
  browseCountry: NonNullable<Definitions["pageData"]>["browseCountry"];
  channel: NonNullable<Definitions["pageData"]>["channel"];
  language: NonNullable<Definitions["pageData"]>["language"];
  platform: NonNullable<Definitions["pageData"]>["deviceType"];
};

export const PageLoadAnalytics = ({
  browseCurrency,
  browseCountry,
  channel,
  language,
  platform,
}: PageLoadAnalyticsProps) => {
  const pathName = usePathname();
  const [previousUrl, setPreviousUrl] = useState<string | null>(null);
  const { pushToDataLayer, resetDataLayer, setSuperProperties } =
    useAnalytics();
  const { status: authStatus, authClient } = useAuth();
  const { countryData } = useStore();

  useWebVitalsAnalytics();

  useEffect(() => {
    const { href, hostname, search: queryString } = window.location;
    const { title, referrer } = window.document;
    const ctaRef = getUrlParameter(CTA_REF_KEY);
    const affidKey = AFFILIATE_ID_KEYS?.find(getUrlParameter);
    const affid = affidKey && getUrlParameter(affidKey);

    const strippedPathname = countryData.siteBasePath
      ? (pathName ?? "").replace(new RegExp(`^${countryData.siteBasePath}`), "")
      : (pathName ?? "");
    const pageIdentity = resolvePageIdentity(
      strippedPathname,
      new URLSearchParams(queryString),
    );
    const shouldSkipSearchPageIdentity =
      pageIdentity?.pageType === "successful search";

    const sendPageData = async () => {
      resetDataLayer();

      if (pageIdentity && !shouldSkipSearchPageIdentity) {
        await setSuperProperties({
          page_type: pageIdentity.pageType,
          page_id: pageIdentity.pageId,
        });
      }

      pushToDataLayer({
        pageData: {
          href: href,
          pagePath: pathName,
          pageTitle: title,
          viewportDimensions: viewportDimensions(),
          localTimestamp: new Date().toISOString(),
          hostname: hostname,
          queryString: queryString,
          browseCurrency: browseCurrency,
          browseCountry: browseCountry,
          language: language,
          channel: channel,
          deviceType: platform,
          referringUrl: previousUrl || referrer,
          env: APP_ENVIRONMENT === "production" ? "prod" : "test",
          webVersion: BUILD_NUMBER || "local",
          ctaRef: ctaRef || undefined,
          marketingData: {
            affid: affid || undefined,
          },
          ...(pageIdentity &&
            !shouldSkipSearchPageIdentity && {
              pageType: pageIdentity.pageType,
              pageName: pageIdentity.pageId,
            }),
        } as Definitions["pageData"],
      });
    };

    sendPageData();
    setPreviousUrl(href);
  }, [pathName]);

  useEffect(() => {
    if (authStatus === "initialising") {
      return;
    }

    const { customerId, isFirstTimeBuyer } = authClient.getIdTokenClaims();

    pushToDataLayer({
      customerIdentityData: {
        customerId,
        firstTimeBuyer: isFirstTimeBuyer,
        geoIpCountryCode: getCookie("geocountry"),
        isAuthenticated: authClient.isAuthenticated(),
      } as Definitions["customerIdentityData"],
    });
  }, [pathName, authStatus]);

  return null;
};
